import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/platform', withCredentials: true });

const inputStyle = {
    padding: '0.6rem 0.75rem', borderRadius: '0.5rem',
    border: '1px solid var(--input-border)', background: 'var(--input-bg)',
    color: 'var(--text-main)', fontSize: '0.85rem',
};

const statusColor = (status) => status === 'SENT' ? '#22c55e' : status === 'FAILED' ? '#ef4444' : '#f59e0b';

// Cross-tenant view of every email the platform has sent (welcome emails,
// password resets, leave/expense notifications) — a system-admin-only
// counterpart to the tenant-scoped "Sent Emails" tab in admin/SystemLogs.jsx.
export default function SentEmailsPanel({ tenants = [] }) {
    const [filters, setFilters] = useState({ tenantId: '', status: '', search: '' });
    const [page, setPage] = useState(1);
    const limit = 50;

    const { data, isLoading } = useQuery({
        queryKey: ['platform-sent-emails', filters, page],
        queryFn: () => {
            const params = new URLSearchParams({ page, limit });
            if (filters.tenantId) params.append('tenantId', filters.tenantId);
            if (filters.status) params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            return api.get(`emails?${params.toString()}`).then(r => r.data);
        },
        placeholderData: keepPreviousData,
    });

    const logs = data?.logs || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / limit));

    const updateFilter = (key, value) => {
        setPage(1);
        setFilters(f => ({ ...f, [key]: value }));
    };

    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
                <select value={filters.tenantId} onChange={e => updateFilter('tenantId', e.target.value)} style={inputStyle}>
                    <option value="">All tenants</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <select value={filters.status} onChange={e => updateFilter('status', e.target.value)} style={inputStyle}>
                    <option value="">All statuses</option>
                    <option value="SENT">Sent</option>
                    <option value="FAILED">Failed</option>
                </select>
                <input
                    placeholder="Search recipient or subject…"
                    value={filters.search}
                    onChange={e => updateFilter('search', e.target.value)}
                    style={{ ...inputStyle, gridColumn: 'span 2' }}
                />
            </div>

            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                {isLoading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                            <tr>
                                <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Timestamp</th>
                                <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Tenant</th>
                                <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Recipient</th>
                                <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Subject</th>
                                <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: 600 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                        No emails found
                                    </td>
                                </tr>
                            ) : (
                                logs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '0.875rem 1rem', fontSize: '0.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                            {new Date(log.createdAt).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '0.875rem 1rem', fontSize: '0.85rem' }}>{log.tenant?.name || '—'}</td>
                                        <td style={{ padding: '0.875rem 1rem', fontSize: '0.85rem' }}>{log.recipientEmail}</td>
                                        <td style={{ padding: '0.875rem 1rem', fontSize: '0.85rem' }}>{log.subject}</td>
                                        <td style={{ padding: '0.875rem 1rem' }}>
                                            <span style={{
                                                padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem',
                                                fontWeight: 600, color: 'white', background: statusColor(log.status),
                                            }}>
                                                {log.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {total > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    <span>Page {page} of {totalPages} ({total} total)</span>
                    <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                        Prev
                    </button>
                    <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}>
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
